import React, { useState, useEffect } from 'react';
import { Save, FolderOpen, Trash2, Plus, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const STORAGE_KEY = 'wheel_saved_wheels';

const SaveLoadWheel = ({ entries, onLoad, variant }) => {
  const { t } = useLanguage();
  const [savedWheels, setSavedWheels] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [activeTab, setActiveTab] = useState('load'); // 'save' | 'load'

  // Load saved wheels from localStorage
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        setSavedWheels(JSON.parse(saved));
      } catch (e) {
        console.error('Error loading saved wheels:', e);
      }
    }
  }, [showModal]);

  // Save current wheel
  const handleSave = () => {
    if (!saveName.trim() || entries.length === 0) return;

    const newWheel = {
      id: `wheel-${Date.now()}`,
      name: saveName.trim(),
      entries: entries.map(e => ({ text: e.text, weight: e.weight, enabled: e.enabled })),
      variant,
      savedAt: new Date().toISOString()
    };

    const updated = [...savedWheels, newWheel];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    setSavedWheels(updated);
    setSaveName('');
    setActiveTab('load');
  };

  // Load a saved wheel
  const handleLoad = (wheel) => {
    onLoad(wheel.entries);
    setShowModal(false);
  };

  // Delete a saved wheel
  const handleDelete = (id) => {
    const updated = savedWheels.filter(w => w.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    setSavedWheels(updated);
  };

  // Format date
  const formatDate = (iso) => {
    const date = new Date(iso);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="btn-action-bar"
        aria-label={t('wheel.saveLoad')}
        title={t('wheel.saveLoad')}
        data-testid="save-load-button"
      >
        <FolderOpen size={20} />
        <span>{t('wheel.saveLoad')}</span>
      </button>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal save-load-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowModal(false)}>
              <X size={20} />
            </button>
            
            <h2>📁 {t('wheel.savedWheels')}</h2>
            
            {/* Tabs */}
            <div className="save-load-tabs">
              <button
                className={`tab ${activeTab === 'load' ? 'active' : ''}`}
                onClick={() => setActiveTab('load')}
              >
                <FolderOpen size={16} />
                {t('wheel.load')} ({savedWheels.length})
              </button>
              <button
                className={`tab ${activeTab === 'save' ? 'active' : ''}`}
                onClick={() => setActiveTab('save')}
              >
                <Save size={16} />
                {t('wheel.saveNew')}
              </button>
            </div>

            {/* Save Tab */}
            {activeTab === 'save' && (
              <div className="save-tab-content">
                <p className="save-info">
                  {t('wheel.saveCurrentWheel', { count: entries.length })}
                </p>
                <div className="save-input-row">
                  <input
                    type="text"
                    value={saveName}
                    onChange={(e) => setSaveName(e.target.value)}
                    placeholder={t('wheel.wheelName')}
                    className="save-name-input"
                    maxLength={50}
                  />
                  <button
                    onClick={handleSave}
                    className="btn-save"
                    disabled={!saveName.trim() || entries.length === 0}
                  >
                    <Save size={16} />
                    {t('wheel.save')}
                  </button>
                </div>
              </div>
            )}

            {/* Load Tab */}
            {activeTab === 'load' && (
              <div className="load-tab-content">
                {savedWheels.length === 0 ? (
                  <div className="no-saved">
                    <FolderOpen size={32} />
                    <p>{t('wheel.noResults')}</p>
                    <small>{t('wheel.saveFirstWheel')}</small>
                  </div>
                ) : (
                  <ul className="saved-wheels-list">
                    {savedWheels.map(wheel => (
                      <li key={wheel.id} className="saved-wheel-item">
                        <div className="wheel-info" onClick={() => handleLoad(wheel)}>
                          <span className="wheel-name">{wheel.name}</span>
                          <span className="wheel-meta">
                            {wheel.entries.length} {t('wheel.entries')} • {formatDate(wheel.savedAt)}
                          </span>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(wheel.id); }}
                          className="btn-delete-wheel"
                          aria-label={t('wheel.removeEntry')}
                        >
                          <Trash2 size={16} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SaveLoadWheel;
